// Query-layer primitives shared by every area (office, onboarding, floor).
//
// Both exports exist because of ONE react-query v5 internal, and the two halves
// only work together. `query.fetch()` honours `cancelRefetch` (the flag
// `invalidateQueries` sets) only when the query ALREADY HAS data:
//
//     // @tanstack/query-core 5.101.4, Query.fetch
//     if (this.state.fetchStatus !== "idle" && this.#retryer?.status() !== "rejected") {
//       if (this.state.data !== void 0 && fetchOptions?.cancelRefetch) {
//         this.cancel({ silent: true });
//       } else if (this.#retryer) {
//         this.#retryer.continueRetry();
//         return this.#retryer.promise;           // ← coalesce, NO new request
//       }
//     }
//
// So invalidating a query whose FIRST fetch is in flight does not refetch — it
// COALESCES into the in-flight read. A read issued BEFORE the mutation landed
// then settles as the "fresh" answer and nothing corrects it: the screen shows
// pre-mutation state indefinitely. That is the M04-S10 floor defect (staging,
// 2026-08-05: tap Start, server records it, screen keeps saying Ready).
//
// The two halves:
//   1. `refreshQuery` cancels FIRST — `cancelQueries` cancels unconditionally,
//      data or not — so the invalidate that follows always issues a request.
//   2. Cancellation only reaches the SOCKET through the AbortSignal react-query
//      hands the queryFn. A queryFn that drops that signal leaves the stale
//      request alive on the wire, and whichever way the race lands the stale
//      response can still be written into the cache. Hence: every queryFn in
//      this app takes `{ signal }` and passes it to `client.GET`.
//
// Pinned by areas/*/hooks/__tests__/*.race.test.tsx (behaviour) and
// __tests__/query-signal.test.ts (no call site regresses).

import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { RequestError } from "./request";

/**
 * The slice of react-query's `QueryFunctionContext` a read needs.
 *
 * Annotate a queryFn parameter with this ONLY where TypeScript's inference
 * needs the help — a `useQuery` whose `refetchInterval` callback reads
 * `query.state.data` makes the options object's type depend on the queryFn's
 * return type, so a destructured-but-uninferred parameter closes the loop and
 * collapses the whole query to `unknown`. Everywhere else the contextual type
 * is inferred and the annotation is noise.
 */
export interface ReadContext {
  readonly signal: AbortSignal;
}

/**
 * Refresh a query after a mutation the server APPLIED: cancel, then invalidate.
 *
 * Use this instead of a bare `invalidateQueries` wherever the target query can
 * plausibly be on its FIRST fetch when the mutation settles — a screen that
 * mutates while it is still loading, a detail the caller has just navigated to,
 * or a background query someone else's screen owns. See the header for why the
 * bare invalidate is not enough there.
 *
 * A bare `invalidateQueries` remains correct — and cheaper — when the mutating
 * screen demonstrably already holds the data it is invalidating, which is the
 * normal office shape (you cannot press Archive on a row you cannot see).
 */
export function refreshQuery(qc: QueryClient, queryKey: QueryKey): Promise<void> {
  return qc.cancelQueries({ queryKey }).then(() => qc.invalidateQueries({ queryKey }));
}

/** react-query's default: three attempts before a read is reported as failed. */
export const READ_RETRIES = 3;

/**
 * THE APP-WIDE READ RETRY POLICY — a refusal is not a blip.
 *
 * react-query's default (`retry: 3`, exponential backoff) treats every rejected
 * queryFn as transient. For a 5xx or a dropped socket that is right. For a
 * **4xx it is wrong twice over**: the server has ANSWERED, and its answer will
 * not change on the next attempt — so the retries are pure waste, and the
 * ~8 seconds they take are spent showing the user a LOADING skeleton for a
 * request that was already decided. What they finally see is a generic error
 * card offering "Retry", which is a control that can never succeed.
 *
 * Measured (surface review, 2026-08-13): a storekeeper opening `/waste/events`
 * — a screen their role does not read — issued `403, 403, 403, 403` over ~8 s
 * behind a skeleton; `/waste/shifts` issued TWELVE (three rollups × four
 * attempts). One request, one refusal, one honest state.
 *
 * The two 4xx exceptions are the ones that are genuinely "come back later":
 *   408 Request Timeout — the request never reached a decision.
 *   429 Too Many Requests — the server is explicitly asking for a retry.
 * (`RequestError.retryAfterMs` carries the server's own delay for callers that
 * honour it; the backoff below is react-query's, not ours to override here.)
 *
 * A 401 never reaches this predicate: `unwrap` throws `UnauthorizedError`
 * instead, and the screens redirect to /login on it.
 */
export function shouldRetryRead(failureCount: number, error: unknown): boolean {
  if (failureCount >= READ_RETRIES) return false;
  if (error instanceof RequestError) {
    const status = error.httpStatus;
    if (status >= 400 && status < 500) return status === 408 || status === 429;
  }
  return true;
}
